"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    regions: Record<string, string>;
    fundA: string;
    fundB: string;
    bottomMessage: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    regions: { us: "米国株", eu: "欧州株", em: "新興国株", jp: "日本株" },
    fundA: "全世界株式ファンド A",
    fundB: "全世界株式ファンド B",
    bottomMessage: "名前が同じでも配分が違う → 中身を確認して選ぶ",
    title: "名前が同じでも中身は違う",
    description: "同じ『全世界株式』でも、国ごとの配分はファンドによって大きく異なります。",
  },
  en: {
    regions: { us: "US equities", eu: "European equities", em: "EM equities", jp: "Japan equities" },
    fundA: "Global Equity Fund A",
    fundB: "Global Equity Fund B",
    bottomMessage: "Same name, different weights → check what is inside before choosing",
    title: "The same name does not mean the same holdings",
    description: "Even for the same “global equity” label, country weights differ a great deal between funds.",
  },
};

// ── Colors by region (aligned with chapter 4) ──────────────────────

const REGION_COLOR = {
  us: COLOR.sky,
  jp: COLOR.rose,
  eu: COLOR.emerald,
  em: "#7c3aed",
};

// ── Assets ─────────────────────────────────────────────────────────

type Asset = { key: string; color: string; weight: number };

const FUND_A: Asset[] = [
  { key: "us", color: REGION_COLOR.us, weight: 65 },
  { key: "eu", color: REGION_COLOR.eu, weight: 25 },
  { key: "em", color: REGION_COLOR.em, weight: 10 },
];

const FUND_B: Asset[] = [
  { key: "us", color: REGION_COLOR.us, weight: 40 },
  { key: "jp", color: REGION_COLOR.jp, weight: 30 },
  { key: "eu", color: REGION_COLOR.eu, weight: 10 },
  { key: "em", color: REGION_COLOR.em, weight: 20 },
];

// ── Circle packing (same as chapter 4) ─────────────────────────────

type Circle = { x: number; y: number; r: number; asset: Asset };

function packCircles(
  assets: Asset[],
  cx: number,
  cy: number,
  areaW: number,
  areaH: number,
  minR: number,
  maxR: number,
): Circle[] {
  const maxWeight = Math.max(...assets.map((a) => a.weight));
  const circles: Circle[] = assets
    .map((asset) => {
      const frac = asset.weight / maxWeight;
      const r = minR + (maxR - minR) * frac;
      return { x: cx, y: cy, r, asset };
    })
    .sort((a, b) => b.r - a.r);

  const angleStep = (Math.PI * 2) / circles.length;
  circles.forEach((c, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const spread = areaW * 0.16;
    c.x = cx + Math.cos(angle) * spread;
    c.y = cy + Math.sin(angle) * spread;
  });

  const halfW = areaW / 2;
  const halfH = areaH / 2;
  for (let iter = 0; iter < 80; iter++) {
    for (let i = 0; i < circles.length; i++) {
      for (let j = i + 1; j < circles.length; j++) {
        const a = circles[i];
        const b = circles[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const minDist = a.r + b.r + 3;
        if (dist < minDist) {
          const push = ((minDist - dist) / dist) * 0.4;
          a.x -= dx * push;
          a.y -= dy * push;
          b.x += dx * push;
          b.y += dy * push;
        }
      }
    }

    for (const c of circles) {
      c.x += (cx - c.x) * 0.06;
      c.y += (cy - c.y) * 0.06;
    }

    for (const c of circles) {
      const yRatio = (c.y - (cy - halfH)) / areaH;
      const taperFactor = 1 - yRatio * 0.1;
      const localHalfW = halfW * taperFactor;
      if (c.x - c.r < cx - localHalfW) c.x = cx - localHalfW + c.r;
      if (c.x + c.r > cx + localHalfW) c.x = cx + localHalfW - c.r;
      if (c.y - c.r < cy - halfH) c.y = cy - halfH + c.r;
      if (c.y + c.r > cy + halfH) c.y = cy + halfH - c.r;
    }
  }

  return circles;
}

// ── SVG primitives ─────────────────────────────────────────────────

function Basket({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const r = 10;
  const rimH = 7;
  return (
    <g>
      <rect x={x - 3} y={y - rimH} width={w + 6} height={rimH} rx={3} fill="currentColor" fillOpacity={0.08} stroke="currentColor" strokeWidth="1" strokeOpacity={0.18} />
      <path
        d={`M ${x} ${y}
            L ${x + 6} ${y + h - r} Q ${x + 6} ${y + h} ${x + 6 + r} ${y + h}
            L ${x + w - 6 - r} ${y + h} Q ${x + w - 6} ${y + h} ${x + w - 6} ${y + h - r}
            L ${x + w} ${y} Z`}
        fill="currentColor"
        fillOpacity={0.02}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.14}
      />
      {[0.3, 0.55, 0.8].map((f) => {
        const ly = y + f * h;
        const inset = 6 + f * 5;
        return <line key={f} x1={x + inset} y1={ly} x2={x + w - inset} y2={ly} stroke="currentColor" strokeWidth="0.5" strokeOpacity={0.06} />;
      })}
    </g>
  );
}

function AssetBubble({ cx, cy, r, asset }: { cx: number; cy: number; r: number; asset: Asset }) {
  const t = STRINGS[useDocLocale()];
  const color = asset.color;
  const isSmall = r < 20;

  if (isSmall) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
        <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="central" fontSize={8} fill={color} fontWeight="700">
          {asset.weight}%
        </text>
        <text x={cx} y={cy + r + 10} textAnchor="middle" fontSize={7} fill={color} fontWeight="600">
          {t.regions[asset.key]}
        </text>
      </g>
    );
  }

  const labelSize = r > 30 ? 10 : 9;
  const pctSize = r > 30 ? 11 : 10;

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.14} stroke={color} strokeWidth="1.2" />
      <text x={cx} y={cy - pctSize * 0.3} textAnchor="middle" dominantBaseline="central" fontSize={labelSize} fill={color} fontWeight="600">
        {t.regions[asset.key]}
      </text>
      <text x={cx} y={cy + labelSize * 0.8} textAnchor="middle" dominantBaseline="central" fontSize={pctSize} fill={color} fontWeight="700">
        {asset.weight}%
      </text>
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function ContentsComparisonIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const basketW = 180;
  const basketH = 160;
  const gap = 40;
  const b1x = (VW - basketW * 2 - gap) / 2;
  const b2x = b1x + basketW + gap;
  const by = 40;
  const VH = by + basketH + 30;

  const circlesA = packCircles(FUND_A, b1x + basketW / 2, by + basketH / 2, basketW - 20, basketH - 24, 14, 42);
  const circlesB = packCircles(FUND_B, b2x + basketW / 2, by + basketH / 2, basketW - 20, basketH - 24, 12, 38);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Fund A */}
      <text x={b1x + basketW / 2} y={by - 14} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.fundA}
      </text>
      <Basket x={b1x} y={by} w={basketW} h={basketH} />
      {circlesA.map((c) => (
        <AssetBubble key={c.asset.key} cx={c.x} cy={c.y} r={c.r} asset={c.asset} />
      ))}

      {/* Fund B */}
      <text x={b2x + basketW / 2} y={by - 14} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.fundB}
      </text>
      <Basket x={b2x} y={by} w={basketW} h={basketH} />
      {circlesB.map((c) => (
        <AssetBubble key={c.asset.key} cx={c.x} cy={c.y} r={c.r} asset={c.asset} />
      ))}

      {/* ≠ divider */}
      <text x={VW / 2} y={by + basketH / 2 + 5} textAnchor="middle" fontSize="24" fill="currentColor" fontWeight="300" fillOpacity={0.15}>
        ≠
      </text>

      {/* Bottom message */}
      <text x={VW / 2} y={by + basketH + 20} textAnchor="middle" fontSize="10" fill="currentColor" fontWeight="600">
        {t.bottomMessage}
      </text>
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFundContentsComparisonDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard title={t.title} description={t.description}>
      <ContentsComparisonIllustration />
    </IllustrationCard>
  );
}
